<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue';
import { Form } from '@/helpers/Form';
import { GridItemAttributes, WidgetModel } from '@/modules/core/dashboards/services/DashboardsService';
import { widgets } from '../widgets';

const emit = defineEmits<{
    (e: 'edit', widget: WidgetModel): void;
    (e: 'delete', widget: WidgetModel): void;
}>();

const props = defineProps<{
    widget: WidgetModel;
    design: boolean;
}>();

const gridStackAttributes = computed<GridItemAttributes>(() => ({
    id: `widget-${props.widget.id}`,
    'gs-id': `widget-${props.widget.id}`,
    'gs-x': props.widget.x,
    'gs-y': props.widget.y,
    'gs-w': props.widget.width,
    'gs-h': props.widget.height,
    'gs-no-resize': true,
}));

const component = computed(() =>
{
    const definition = widgets.find(p => p.key == props.widget.type);

    if (definition)
        return defineAsyncComponent(definition.value);

    return null;
});

const changeSettings = () => emit('edit', props.widget);
const deleteWidget = () => emit('delete', props.widget);
</script>

<template>
    <div class="grid-stack-item" v-bind="gridStackAttributes">
        <div class="grid-stack-item-content" :class="{ 'move': props.design }">
            <div v-if="props.widget" class="widget" :style=" props.widget.backgroundColor != null ? { backgroundColor: props.widget.backgroundColor } : null">
                <div class="header">
                    <h4>{{ props.widget.name }}</h4>

                    <IdeoDropdown v-if="props.design" variant="none" dropleft no-caret class="options">
                        <template #button-content>
                            <i class="icon far fa-ellipsis-v"></i>
                        </template>
                        <template #default>
                            <IdeoDropdownItemButton @click="changeSettings">
                                <i class="icon far fa-gear"></i>
                                <span>{{ $t('[[[Ustawienia]]]') }}</span>
                            </IdeoDropdownItemButton>
                            <IdeoDropdownItemButton
                                :message="$t('[[[Czy na pewno chcesz usunąć widget?]]]')"
                                :value="props.widget.id"
                                message-placement="left"
                                @confirm="deleteWidget"
                            >
                                <i class="icon far fa-trash-alt"></i> {{ $t('[[[Usuń]]]') }}
                            </IdeoDropdownItemButton>
                        </template>
                    </IdeoDropdown>
                </div>
                <div class="content scroll">
                    <template v-if="component">
                        <component :is="component" :model-value="Form.create<WidgetModel>(props.widget)" mode="view"></component>
                    </template>
                    <span v-else>{{ $t('[[[Nie znaleziono pasującego widgetu]]]') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.grid-stack-item-content {
    display: flex;
    flex-direction: column;
    position: relative;

    .widget {
        background: var(--bs-body-bg);
        padding: 25px 15px;
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: 5px;
        flex-grow: 1;
        display: flex;
        overflow: hidden;
        flex-direction: column;

        &.smaller-padding {
            padding: 10px;
        }

        .header {
            margin-bottom: 15px;
            justify-content: space-between;
            display: flex;
            gap: 10px;
            align-items: center;

            &.smaller-margin {
                margin-bottom: 10px;
            }

            h4 {
                line-height: 1.5rem;
                font-size: 1.25rem;
                font-weight: 700;
                margin: 0;

                &.smaller-header {
                    line-height: 1.5rem;
                    font-size: 1rem;
                }
            }
        }

        .icon {
            font-size: 1rem;
        }

        .options {
            margin: -6px -12px;

            :deep(button) {
                margin: 0;

                &:active {
                    border-color: transparent;
                }
            }
        }

        .content {
            flex: 1 1 100%;
            display: flex;
            flex-direction: column;
        }
    }

    &.move {
        cursor: move;
    }

    :deep(.link-more) {
        display: inline-block;
        margin-top: 15px;
        font-size: 1rem;
        line-height: 1.75rem;
        color: var(--bs-primary);
    }
}
</style>
